/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/EnterpriseServerUrl" />
import * as React from "react";
import { equals, format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Uri } from "VSS/Platform/Util/Url";
import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { IEnterpriseServerUrlProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/EnterpriseServerUrl.Props";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";

export interface IEnterpriseServerUrlState {
    enableSslCheckbox: boolean;
}

export class EnterpriseServerUrl extends VssComponent<IEnterpriseServerUrlProps, IEnterpriseServerUrlState> {
    private readonly _textFieldRef = React.createRef<TextField>();

    constructor(props: IEnterpriseServerUrlProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            enableSslCheckbox: this._isUrlHttps(this.props.url)
        };
    }

    public render() {
        return (
            <>
                <div className="fontWeightSemiBold">{Resources.EnterpriseServerUrl}</div>
                <FormItem className="input-field" error={!!this.props.errorMessage} message={this.props.errorMessage}>
                    <TextField
                        ariaLabel={Resources.EnterpriseServerUrl}
                        onChange={this._onUrlInputChanged}
                        placeholder={Resources.EnterpriseServerUrl_Add}
                        value={this.props.url}
                        onKeyDown={this.props.onKeyDown}
                        ref={this._textFieldRef}
                    />
                </FormItem>
                <div className="input-description">{format(Resources.EnterpriseServerUrl_Example, GitHubConnectionConstants.FabrikamUrl)}</div>
                <Checkbox
                    className="accept-untrusted-ssl-check-box"
                    checked={this.props.acceptUntrustedSsl}
                    onChange={this._onSslChange}
                    label={Resources.EnterpriseOAuth_acceptSsl}
                    disabled={!this.state.enableSslCheckbox}
                />
            </>
        );
    }

    public componentDidMount(): void {
        requestAnimationFrame(() => {
            if (this._textFieldRef.current) {
                this._textFieldRef.current.focus();
            }
        });
    }

    private readonly _onUrlInputChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        const shouldEnableSslCheckbox = this._isUrlHttps(value);
        if (this.state.enableSslCheckbox !== shouldEnableSslCheckbox) {
            this.setState({ enableSslCheckbox: shouldEnableSslCheckbox }, () => {
                if (!shouldEnableSslCheckbox && this.props.acceptUntrustedSsl) {
                    // Uncheck the accept untrusted ssl if url is not https
                    const { onSslChange } = this.props;
                    onSslChange && onSslChange(false);
                }
            });
        }

        // On input change
        const { onEnterpriseUrlInputChanged } = this.props;
        onEnterpriseUrlInputChanged && onEnterpriseUrlInputChanged(value);
    };

    private readonly _onSslChange = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean): void => {
        // Update untrusted ssl checkbox state
        const acceptUntrustedSsl = this.state.enableSslCheckbox && checked;
        const { onSslChange } = this.props;
        onSslChange && onSslChange(acceptUntrustedSsl);
    };

    private _isUrlHttps(url: string): boolean {
        let isHttps = false;
        if (url) {
            const uri = new Uri(url);
            isHttps = equals(uri.scheme, "https", true);
        }
        return isHttps;
    }
}
